import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface DangerousConfirmProps {
  trigger: React.ReactNode;
  title: string;
  description: string;
  confirmText: string;
  expectedValue: string;
  onConfirm: () => void | Promise<void>;
  loading?: boolean;
}

export const DangerousConfirm = ({
  trigger,
  title,
  description,
  confirmText,
  expectedValue,
  onConfirm,
  loading,
}: DangerousConfirmProps) => {
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const matches = value === expectedValue;

  const handleConfirm = async () => {
    if (!matches) return;
    await onConfirm();
    setValue('');
    setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2 py-2">
          <p className="text-sm text-muted-foreground">Type <span className="font-medium text-foreground">{expectedValue}</span> to confirm.</p>
          <Input value={value} onChange={(event) => setValue(event.target.value)} autoComplete="off" />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button variant="destructive" disabled={!matches || loading} onClick={handleConfirm}>
              {loading ? 'Working...' : confirmText}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
